/* eslint-disable @typescript-eslint/no-throw-literal */
import { Injectable, OnDestroy } from '@angular/core';
import { ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, take, timeout } from 'rxjs/operators';
import { Dictionary, ListCached, Parameter } from 'projects/common/src/app/classes';
import { IError } from 'projects/common/src/app/interfaces';
import { IParameter } from '../interfaces/parameter';
import { CacheService } from './cache.service';
import { Logger } from 'projects/common/src/app/classes/logger';
import { IParameterService } from '../interfaces/parameter-service';

/**
 * Clase para el manejo de parámetros del sistema
 * Consolida todos los parámetros para un único punto de acceso.
 * Los servicios deberán suscribirse para que esta clase los provea.
 */
@Injectable({
  providedIn: 'root',
})
export class ParameterHandlerService extends Logger implements OnDestroy {
  /**
   * Coleccion de los valores de parámetro
   */
  public parameters: Dictionary<IParameter>;

  /**
   * Colección de observable a los parámetros
   */
  public parameterSource: Dictionary<ReplaySubject<any>>;

  /**
   * Colección de referencias a suscripciones
   * Se utiliza para desuscribir al momento de destruir la instancia del servicio
   */
  private subscriptionCollection: Subscription[];
  private readonly parameterCached: ListCached<string>;

  constructor(private cacheService: CacheService) {
    super();
    this.parameterCached = new ListCached(this.name, this.cacheService);
    this.subscriptionCollection = [];
    this.parameters = new Dictionary();
    this.parameterSource = new Dictionary();
  }

  /**
   * Callback que se ejecuta al eliminar la instacia de la clase
   */
  public ngOnDestroy(): void {
    for (const subscription of this.subscriptionCollection) {
      subscription.unsubscribe();
    }
  }

  /**
   * Suscribe un servicio para proveer los valor de su parámertro
   *
   * @param serviceReference referencia a un servicio que provee un parámetro
   */
  public subscribe(serviceReference: IParameterService<any>, cacheable = false, inLocalStorage = false): void {
    if (!this.parameters.containsKey(serviceReference.name)) {
      const parameter = {
        serviceReference,
        subjectSource: serviceReference.getObservable(),
        cacheable,
        inLocalStorage,
      } as IParameter;
      this.parameters.add(serviceReference.name, parameter);
      const paramSubject = new ReplaySubject(1);
      this.parameterSource.add(serviceReference.name, paramSubject);

      const subscription = serviceReference.getObservable().subscribe((value: any) => {
        this.setParameter(serviceReference.name, value);
      });
      this.subscriptionCollection.push(subscription);

      paramSubject.pipe(distinctUntilChanged());

      // TODO mensaje a configuracion
      this.log(`Parámetro [${serviceReference.name}] suscrito.`);
    } else {
      // TODO mensaje a configuracion
      this.warn(`Servicio [${serviceReference.name}] ya registrado`);
    }
  }

  /**
   * Retorna una promesa que resolverá el valor del parámetro dado en el argumento.
   * Si el valor no se encuentra diponible retornará con el valor 'null'.
   *
   * @param key identificador de un parámetro suscrito
   */
  public getParameter(key: string): Promise<any> {
    return new Promise((resolve) => {
      if (this.parameters.containsKey(key)) {
        const value = this.parameters.item(key);
        if (value) {
          resolve(this.parameters.item(key));
        } else {
          // Si el valor no se encuentra disponible en la colección de parámetros
          // Se solicitará al observable asociado al argumento 'key'. Si el parámetro
          // toma más de 5 segundos se resolverá con un 'null'
          const source = this.parameterSource.item(key);
          source.pipe(take(1), timeout(5000)).subscribe((x: any) => resolve(x));
        }
      } else {
        this.error(`Servicio [${key}] aún no ha sido inicializado.`);
        resolve(null);
      }
    });
  }

  /**
   * Retorna una referencia al servicio requerido
   *
   * @param key nombre del servicio requerido
   */
  public getService(key: string): Parameter<any> {
    if (this.parameters.containsKey(key)) {
      return this.parameters.item(key).serviceReference;
    }
    throw {
      message: 'SERVICE_NOT_REGISTERED',
      extra: key,
    } as IError;
  }

  /**
   * Asigna un valor dado a un parámetro indicado en le llave 'key'.
   * Este valor se podrá reestablecer al reiniciar el navegador.
   *
   * @param key identificador para un parámetro
   * @param value valor que se asignará al parámetro
   * @param cached indica si se guardará el valor en memoria para reestablecer al recargar
   */
  public setParameter(key: string, value: any): void {
    if (this.parameters.containsKey(key)) {
      const parameter = this.parameters.item(key);
      const oldValue = parameter.value;
      if (parameter.breakNextValue) {
        parameter.breakNextValue = false;
        this.parameterSource.item(key).next(oldValue);
      } else if (oldValue !== value) {
        parameter.value = value;
        if (parameter.cacheable) {
          this.parameterCached.add(key);
          if (parameter.inLocalStorage) {
            this.cacheService.saveLocal(key, value);
          } else {
            this.cacheService.saveSession(key, value);
          }
        }
        this.parameterSource.item(key).next(value);
      }
    } else {
      this.error(`Servicio [${key}] aún no ha sido inicializado.`);
    }
  }

  /**
   * Restaura los valores suscritos marcados como cacheable
   */
  public restore(): void {
    const parameters = this.parameterCached.get();
    for (const key of parameters || []) {
      let element = this.cacheService.local(key);
      if (element) {
        this.setParameter(key, element);
      } else {
        element = this.cacheService.session(key);
        if (element) {
          this.setParameter(key, element);
        }
      }
    }
  }
}
